import {
  combineClasses,
  CoreTokens,
  ErrorTokens,
  StoryNotFoundError,
  Trans,
  Translation,
} from "@citychallenge/core";
import { FC, useMemo } from "react";

import { Button } from "../../ui/button";
import { ErrorAuth } from "./error-auth";
import styles from "./error-view.module.scss";
import { SvgErrorIcon } from "./svg-error-icon";

export interface ErrorViewProps {
  header?: Translation | string;
  error?: Error | string;
  retry?: Translation | string;

  onRetry?: () => void;
  className?: string;
}

interface LocalizedError extends Error {
  localizedMessage?: Translation | string;
}

export function getErrorMessage(error: LocalizedError | string | undefined) {
  if (typeof error === "string") return ErrorTokens[error] || error;

  if (error instanceof StoryNotFoundError) {
    return undefined;
  }
  if (error instanceof Error) {
    return (
      error.localizedMessage || ErrorTokens[error.message] || error.message
    );
  }

  return error || CoreTokens.unknownError;
}

export const ErrorView: FC<ErrorViewProps> = ({
  header,
  error,
  retry,
  onRetry,
  className,
}) => {
  function getHeader() {
    if (error instanceof StoryNotFoundError) {
      return error.message;
    }
    return undefined;
  }

  const isAuthError = useMemo(() => {
    if (error instanceof Error) {
      // ERR_UNAUTHORIZED reaches a view only when the app deliberately kept its
      // session (the request carried some other token); the way out is still
      // signing in.
      if (
        error.message === "ERR_NO_DIVISION_ACCESS" ||
        error.message === "ERR_UNAUTHORIZED"
      ) {
        return true;
      }
    }
    return false;
  }, [error]);

  return (
    <div
      className={combineClasses(styles.wrapper, className)}
      data-error-view-wrapper
    >
      <div className={styles.icon}>
        <SvgErrorIcon />
      </div>
      <h3>
        <Trans>{header || getHeader() || CoreTokens.somethingWrong}</Trans>
      </h3>
      <p>
        <Trans>{getErrorMessage(error)}</Trans>
      </p>

      {onRetry ? (
        <Button className={styles.button} onClick={onRetry}>
          {retry || CoreTokens.tryAgain}
        </Button>
      ) : null}

      {isAuthError ? <ErrorAuth /> : null}
    </div>
  );
};

export default ErrorView;
